import { Controller } from "@hotwired/stimulus"

// Debounced auto-save for the discovery questionnaire. Every change PATCHes
// the form to the discovery update action; the Save button doubles as the
// status indicator: blue "Save Discovery" when there are unsaved edits,
// "Saving…" mid-flight, green "Saved" once persisted. The button still works
// as a manual submit fallback if JS autosave ever fails.
//
// Trix rewrites loaded HTML (HubSpot <p> becomes <div>) and emits
// trix-change. Snapshot the form after that rewrite so a load does not
// PATCH; only a real edit (FormData differs from the snapshot) saves.
export default class extends Controller {
  static targets = ["saveButton"]
  static values = {
    url: String,
    debounceMs: { type: Number, default: 800 }
  }

  connect() {
    this.boundCapture = this.captureBaseline.bind(this)
    this.element.addEventListener("trix-initialize", this.boundCapture)

    const editors = [...this.element.querySelectorAll("trix-editor")]
    this.baselinePending = editors.some((el) => !el.editor)
    if (!this.baselinePending) this.captureBaseline()
  }

  captureBaseline() {
    const editors = [...this.element.querySelectorAll("trix-editor")]
    if (editors.some((el) => !el.editor)) return

    // Trix fires trix-initialize in rAF after loadHTML; wait one more frame
    // so the hidden input holds the normalized document.
    requestAnimationFrame(() => {
      this.snapshot = this.serialize()
      this.baselinePending = false
    })
  }

  scheduleSave() {
    if (this.baselinePending || this.snapshot == null || this.serialize() === this.snapshot) return

    this.markDirty()
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.debounceMsValue)
  }

  async save() {
    if (!this.hasSaveButtonTarget) return
    this.setState("saving")

    // Supersede any in-flight save so a slow earlier request can't overwrite
    // the status of a newer edit.
    if (this.inflight) this.inflight.abort()
    const ac = new AbortController()
    this.inflight = ac

    try {
      const response = await fetch(this.urlValue, {
        method: "POST", // form carries the _method=patch override
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: new FormData(this.element),
        credentials: "same-origin",
        redirect: "manual",
        signal: ac.signal
      })

      // A redirect (opaqueredirect) or 2xx both mean the save succeeded.
      if (response.ok || response.type === "opaqueredirect" || response.status === 0) {
        this.snapshot = this.serialize()
        this.setState("saved")
      } else {
        this.setState("dirty")
      }
    } catch (err) {
      if (err.name === "AbortError") return
      this.setState("dirty")
    } finally {
      if (this.inflight === ac) this.inflight = null
    }
  }

  markDirty() {
    this.setState("dirty")
  }

  setState(state) {
    if (!this.hasSaveButtonTarget) return
    const btn = this.saveButtonTarget
    const states = {
      dirty: ['<i class="fa-regular fa-floppy-disk me-1"></i>Save Discovery', "btn-primary"],
      saving: ['<i class="fa-regular fa-spinner fa-spin me-1"></i>Saving…', "btn-primary"],
      saved: ['<i class="fa-regular fa-circle-check me-1"></i>Saved', "btn-success"]
    }
    const [html, cls] = states[state] || states.dirty
    btn.innerHTML = html
    btn.classList.remove("btn-primary", "btn-success")
    btn.classList.add(cls)
  }

  serialize() {
    return new URLSearchParams(new FormData(this.element)).toString()
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  disconnect() {
    this.element.removeEventListener("trix-initialize", this.boundCapture)
    if (this.timer) clearTimeout(this.timer)
    if (this.inflight) this.inflight.abort()
  }
}
